import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getSubjects, getChapters, createChapter, getVideos, createVideo, updateVideo, deleteVideo, reorderVideos } from '../../api';

function SortableVideo({ v, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: v.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={{ ...style, display: 'flex', alignItems: 'center', gap: 12,
      padding: 14, border: '1px solid var(--border)', borderRadius: 10, background: 'white', marginBottom: 8 }}>
      <span {...attributes} {...listeners} style={{ cursor: 'grab', color: 'var(--text-muted)', fontSize: 18, userSelect: 'none' }}>⠿</span>
      <span style={{ fontSize: 20 }}>🎬</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{v.title}</div>
        {v.description && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.description}</div>}
        <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 2 }}>{v.youtube_url.slice(0, 50)}…</div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => onEdit(v)}>Edit</button>
        <button className="btn btn-danger btn-sm" onClick={() => onDelete(v.id)}>Del</button>
      </div>
    </div>
  );
}

const emptyV = { chapter_id: '', title: '', description: '', youtube_url: '' };

export default function AdminVideos() {
  const qc = useQueryClient();
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedChapter, setSelectedChapter] = useState('');
  const [form, setForm] = useState(emptyV);
  const [formSubject, setFormSubject] = useState('');
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [videos, setVideos] = useState([]);
  const [newChapterName, setNewChapterName] = useState('');

  const sensors = useSensors(useSensor(PointerSensor));
  const subjects = useQuery({ queryKey: ['subjects'], queryFn: () => getSubjects().then(r => r.data) });
  const chapters = useQuery({ queryKey: ['chapters', selectedSubject], queryFn: () => getChapters(selectedSubject).then(r => r.data), enabled: !!selectedSubject });
  const formChapters = useQuery({ queryKey: ['chapters', formSubject], queryFn: () => getChapters(formSubject).then(r => r.data), enabled: !!formSubject });
  const videosQ = useQuery({
    queryKey: ['videos', selectedChapter],
    queryFn: () => getVideos(selectedChapter).then(r => { setVideos(r.data); return r.data; }),
    enabled: !!selectedChapter,
  });

  const createChapterMut = useMutation({ mutationFn: ({ sid, name }) => createChapter(sid, { name, subject_id: sid }), onSuccess: () => qc.invalidateQueries(['chapters']) });
  const createV = useMutation({ mutationFn: createVideo, onSuccess: () => { qc.invalidateQueries(['videos', selectedChapter]); setForm(emptyV); setShowForm(false); } });
  const updateV = useMutation({ mutationFn: ({ id, data }) => updateVideo(id, data), onSuccess: () => { qc.invalidateQueries(['videos', selectedChapter]); setEditing(null); setShowForm(false); } });
  const deleteV = useMutation({ mutationFn: deleteVideo, onSuccess: () => qc.invalidateQueries(['videos', selectedChapter]) });
  const reorderV = useMutation({ mutationFn: reorderVideos });

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = videos.findIndex(v => v.id === active.id);
    const newIndex = videos.findIndex(v => v.id === over.id);
    const newOrder = arrayMove(videos, oldIndex, newIndex);
    setVideos(newOrder);
    reorderV.mutate({ videos: newOrder.map((v, i) => ({ id: v.id, position: i })) });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { ...form, chapter_id: parseInt(form.chapter_id) };
    if (editing) updateV.mutate({ id: editing, data: { title: payload.title, description: payload.description, youtube_url: payload.youtube_url } });
    else createV.mutate(payload);
  };

  const startEdit = (v) => { setEditing(v.id); setShowForm(true); setForm({ chapter_id: v.chapter_id, title: v.title, description: v.description || '', youtube_url: v.youtube_url }); };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div><h1 style={{ fontSize: 26 }}>Videos</h1><p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Manage and reorder videos by chapter</p></div>
        <button className="btn btn-primary" onClick={() => { setShowForm(true); setEditing(null); setForm(emptyV); }}>+ Add Video</button>
      </div>

      {/* Browse filter */}
      <div className="card" style={{ marginBottom: 20, padding: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <select className="form-select" style={{ width: 180 }} value={selectedSubject}
            onChange={e => { setSelectedSubject(e.target.value); setSelectedChapter(''); }}>
            <option value="">Select Subject</option>
            {subjects.data?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {selectedSubject && (
            <select className="form-select" style={{ width: 200 }} value={selectedChapter} onChange={e => setSelectedChapter(e.target.value)}>
              <option value="">Select Chapter</option>
              {chapters.data?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 20 }}>{editing ? 'Edit Video' : 'Add New Video'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">Subject *</label>
                <select className="form-select" value={formSubject} onChange={e => { setFormSubject(e.target.value); setForm(f => ({...f, chapter_id: ''})); }}>
                  <option value="">Select</option>
                  {subjects.data?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Chapter *</label>
                <select className="form-select" value={form.chapter_id} disabled={!formSubject}
                  onChange={e => setForm(f => ({...f, chapter_id: e.target.value}))}>
                  <option value="">Select</option>
                  {formChapters.data?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {formSubject && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    <input className="form-input" style={{ flex: 1 }} placeholder="New chapter name" value={newChapterName} onChange={e => setNewChapterName(e.target.value)} />
                    <button className="btn btn-ghost btn-sm" type="button" onClick={() => { if(newChapterName) { createChapterMut.mutate({ sid: parseInt(formSubject), name: newChapterName }); setNewChapterName(''); } }}>+ Add</button>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input className="form-input" value={form.title} required onChange={e => setForm(f => ({...f, title: e.target.value}))} />
              </div>
              <div className="form-group">
                <label className="form-label">YouTube URL *</label>
                <input className="form-input" value={form.youtube_url} required placeholder="https://youtube.com/watch?v=..." onChange={e => setForm(f => ({...f, youtube_url: e.target.value}))} />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="form-label">Description</label>
              <textarea className="form-textarea" value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" type="submit">{editing ? 'Update' : 'Add Video'}</button>
              <button className="btn btn-ghost" type="button" onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {selectedChapter && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3>Videos in chapter <span style={{ color: 'var(--primary)' }}>({videos.length})</span></h3>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Drag ⠿ to reorder</span>
          </div>
          {videosQ.isLoading ? <div className="spinner"></div> : videos.length === 0 ? (
            <div className="empty-state">No videos in this chapter yet.</div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={videos.map(v => v.id)} strategy={verticalListSortingStrategy}>
                {videos.map(v => <SortableVideo key={v.id} v={v} onEdit={startEdit} onDelete={id => window.confirm('Delete?') && deleteV.mutate(id)} />)}
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}
    </div>
  );
}
